import { http } from './http';
import { mergeAuthUserPayload } from '../utils/mapUser';
import { unwrapData, unwrapList } from '../utils/unwrapApiData';

function organizationsFromAuthPayload(inner) {
  if (!inner || typeof inner !== 'object') return [];
  if (Array.isArray(inner.organizations)) return inner.organizations;
  if (inner.organizations && typeof inner.organizations === 'object') {
    return unwrapList(inner.organizations);
  }
  return [];
}

/**
 * وب‌سرویس‌های Auth — مطابق `src/auth.txt`
 * پاسخ استاندارد: `{ success, message, data }`
 */

/**
 * @param {*} payload — بدنهٔ پاسخ API (مثلاً `response.data`)
 * @returns {{ token: string|null, user: *, organizations: unknown[], current_organization_id: string|null } | null}
 */
export function parseLoginPayload(payload) {
  const inner = unwrapData(payload) ?? payload;
  if (!inner || typeof inner !== 'object') return null;
  const token = typeof inner.token === 'string' ? inner.token : null;
  const user = inner.user ? mergeAuthUserPayload(inner.user, inner) : null;
  const organizations = organizationsFromAuthPayload(inner);
  const current_organization_id =
    inner.current_organization_id != null ? String(inner.current_organization_id) : null;
  const requires_active_organization =
    inner.requires_active_organization != null ? Boolean(inner.requires_active_organization) : true;
  const is_platform_super_admin =
    inner.is_platform_super_admin != null
      ? Boolean(inner.is_platform_super_admin)
      : inner.user?.is_platform_super_admin != null
        ? Boolean(inner.user.is_platform_super_admin)
        : undefined;
  const permissions = Array.isArray(inner.permissions) ? inner.permissions : [];
  const platform_roles = Array.isArray(inner.platform_roles) ? inner.platform_roles : [];
  const organization_permissions = Array.isArray(inner.organization_permissions)
    ? inner.organization_permissions
    : [];
  const organization_access_scopes = Array.isArray(inner.organization_access_scopes)
    ? inner.organization_access_scopes
    : [];
  const active_organization_id =
    inner.active_organization_id != null ? String(inner.active_organization_id) : null;
  const requires_two_factor = Boolean(inner.requires_two_factor);
  const two_factor_challenge_token =
    typeof inner.two_factor_challenge_token === 'string' ? inner.two_factor_challenge_token : null;
  const two_factor_expires_at = inner.two_factor_expires_at ?? null;
  return {
    token,
    user,
    organizations,
    current_organization_id,
    active_organization_id,
    requires_active_organization,
    is_platform_super_admin,
    permissions,
    platform_roles,
    organization_permissions,
    organization_access_scopes,
    requires_two_factor,
    two_factor_challenge_token,
    two_factor_expires_at,
  };
}

export function extractTokenFromLoginResponse(payload) {
  return parseLoginPayload(payload)?.token ?? null;
}

/**
 * @param {string} identifier
 * @param {string} password
 * @param {string} [organizationId] — uuid اختیاری
 */
export async function loginWithPassword(identifier, password, organizationId, captcha = {}) {
  const body = { identifier, password, ...captcha };
  if (organizationId) body.organization_id = organizationId;
  const { data } = await http.post('/auth/login', body);
  return data;
}

export async function requestRegisterOtp(mobile, captcha = {}) {
  const { data } = await http.post('/auth/register/otp', { mobile, ...captcha });
  return data;
}

export async function registerAccount({ name, mobile, password, otp, email, captcha = {} }) {
  const body = { name, mobile, password, otp, ...captcha };
  if (email) body.email = email;
  const { data } = await http.post('/auth/register', body);
  return data;
}

/**
 * @param {string} identifier — ایمیل، موبایل یا نام کاربری
 * @param {object} [captcha]
 */
export async function requestOtp(identifier, captcha = {}) {
  const { data } = await http.post('/auth/otp/request', { identifier, ...captcha });
  return data;
}

/**
 * @param {string} identifier
 * @param {string} otp — ۶ رقم
 * @param {string} [organizationId]
 */
export async function verifyOtp(identifier, otp, organizationId) {
  const body = { identifier, otp };
  if (organizationId) body.organization_id = organizationId;
  const { data } = await http.post('/auth/otp/verify', body);
  return data;
}

export async function completeTwoFactorLogin(challengeToken, code) {
  const { data } = await http.post('/auth/2fa/challenge', {
    challenge_token: challengeToken,
    code,
  });
  return data;
}

/**
 * @param {import('axios').AxiosRequestConfig} [config]
 * @returns {Promise<{ user: *, organizations: unknown[], current_organization_id: string|null }>}
 */
export async function fetchMePayload(config) {
  const { data } = await http.get('/auth/me', config);
  const inner = unwrapData(data) ?? data;
  if (inner && typeof inner === 'object' && 'user' in inner) {
    return {
      user: mergeAuthUserPayload(inner.user, inner),
      organizations: organizationsFromAuthPayload(inner),
      current_organization_id:
        inner.current_organization_id != null ? String(inner.current_organization_id) : null,
      requires_active_organization:
        inner.requires_active_organization != null ? Boolean(inner.requires_active_organization) : undefined,
      is_platform_super_admin:
        inner.is_platform_super_admin != null
          ? Boolean(inner.is_platform_super_admin)
          : inner.user?.is_platform_super_admin != null
            ? Boolean(inner.user.is_platform_super_admin)
            : undefined,
      permissions: Array.isArray(inner.permissions) ? inner.permissions : [],
      platform_roles: Array.isArray(inner.platform_roles) ? inner.platform_roles : [],
      organization_permissions: Array.isArray(inner.organization_permissions)
        ? inner.organization_permissions
        : [],
      organization_access_scopes: Array.isArray(inner.organization_access_scopes)
        ? inner.organization_access_scopes
        : [],
      active_organization_id:
        inner.active_organization_id != null ? String(inner.active_organization_id) : null,
      is_impersonating: inner.is_impersonating != null ? Boolean(inner.is_impersonating) : undefined,
      impersonation_locked_organization_id:
        inner.impersonation_locked_organization_id != null
          ? String(inner.impersonation_locked_organization_id)
          : null,
    };
  }
  return {
    user: inner && typeof inner === 'object' ? mergeAuthUserPayload(inner, inner) : inner,
    organizations: [],
    current_organization_id: null,
    requires_active_organization: undefined,
  };
}

export async function fetchOrganizations(config) {
  const { data } = await http.get('/auth/organizations', config);
  const inner = unwrapData(data) ?? data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner) && Array.isArray(inner.organizations)) {
    return inner.organizations;
  }
  return unwrapList(data);
}

/** @returns {Promise<unknown[]>} لیست سازمان‌ها پس از اطمینان از فضای شخصی */
export async function ensurePersonalWorkspace(config) {
  const { data } = await http.post('/auth/ensure-personal-workspace', null, config);
  return unwrapList(data);
}

/** @returns {Promise<{ organization: *, organizations: unknown[], active_organization_id: string }>} */
export async function createPersonalOrganization(body, config) {
  const { data } = await http.post('/auth/personal-organization', body, config);
  return unwrapData(data) ?? data;
}

export async function switchOrganization(organizationId) {
  if (organizationId == null || organizationId === '' || String(organizationId) === 'NaN') {
    throw new Error('شناسه سازمان معتبر نیست.');
  }
  await http.post(`/auth/switch-organization/${String(organizationId)}`);
}

export async function fetchLoginPanelConfig(config) {
  const { data } = await http.get('/auth/login-panel', config);
  return unwrapData(data) ?? data;
}

export async function fetchCaptchaConfig(config) {
  const { data } = await http.get('/auth/captcha-config', config);
  return unwrapData(data) ?? data;
}

export async function fetchMewsCaptchaImage(config) {
  const { data } = await http.get('/site/captcha/image', config);
  return unwrapData(data) ?? data;
}

export async function logout() {
  await http.post('/auth/logout');
}

/** باطل کردن توکن impersonation فعلی — `POST /api/v1/auth/stop-impersonation` */
export async function stopImpersonation() {
  const { data } = await http.post('/auth/stop-impersonation');
  return data;
}
